import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { assessmentsApi, handleApiError } from '../lib/api-client';
import type { CreateAssessmentDto, UpdateAssessmentSignatureDto } from '../types/api';

export const useAssessments = (params?: {
  userId?: string;
  userIds?: string[];
  facilityId?: string;
  locationId?: string;
  facilityTypeId?: string;
  questionnaireId?: string;
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  weekNumber?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['assessments', params],
    queryFn: () => assessmentsApi.getAll(params),
  });
};

export const useAssessment = (id: string) => {
  return useQuery({
    queryKey: ['assessments', id],
    queryFn: () => assessmentsApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateAssessment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAssessmentDto) => assessmentsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      toast.success('Assessment created successfully');
    },
    onError: handleApiError,
  });
};

export const useUpdateAssessmentSignature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAssessmentSignatureDto }) =>
      assessmentsApi.updateSignature(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      toast.success('Signature updated successfully');
    },
    onError: handleApiError,
  });
};

export const useUnassessedFacilities = (params?: {
  weekNumber?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
  locationId?: string;
  facilityTypeId?: string;
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['unassessed-facilities', params],
    queryFn: () => assessmentsApi.getUnassessed(params),
  });
};

export const useFines = (params?: {
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  page?: number;
  limit?: number;
  search?: string;
}) => {
  return useQuery({
    queryKey: ['assessments', 'fines', params],
    queryFn: () => assessmentsApi.getFines(params),
  });
};

export const useMarkFineCollected = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, collected }: { id: string; collected: boolean }) =>
      assessmentsApi.markFineCollected(id, collected),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments', 'fines'] });
    },
    onError: () => toast.error('Failed to update fine status'),
  });
};

export const useFlaggedAssessments = (params?: {
  locationId?: string;
  assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: ['assessments', 'flagged', params],
    queryFn: () => assessmentsApi.getFlagged(params),
  });
};

export const useUpdateAssessmentFlag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isFlagged }: { id: string; isFlagged: boolean }) =>
      assessmentsApi.updateFlag(id, { isFlagged }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments'] });
      queryClient.invalidateQueries({ queryKey: ['assessments', 'flagged'] });
      toast.success('Flag updated');
    },
    onError: handleApiError,
  });
};

export const useUpdateRecommendationResolved = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ recommendationId, resolved }: { recommendationId: string; resolved: boolean }) =>
      assessmentsApi.updateRecommendationResolved(recommendationId, resolved),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-recommendations'] });
      toast.success('Recommendation updated');
    },
    onError: handleApiError,
  });
};
